/**
 * Browser mail cache — IndexedDB + a small localStorage bootstrap so Mail
 * paints on app start without waiting for the network (or even IDB open).
 *
 * Native apps store on disk; the Hub browser SPA uses origin-scoped storage
 * in that browser profile. Clearing site data wipes it.
 */

import type { MailAccount, MailThread } from "@/lib/api";

const DB_NAME = "atw-hub-mail-cache";
const DB_VERSION = 2;
const LIST_STORE = "lists";
const DETAIL_STORE = "details";
const ACCOUNTS_STORE = "accounts";

const BOOTSTRAP_KEY = "atw-hub-mail-bootstrap";
const NAV_KEY = "atw-hub-mail-nav";
const CACHE_WINDOW_KEY = "atw-hub-mail-cache-window";

/** Cap sync bootstrap size — enough for first paint, not the whole mailbox. */
const BOOTSTRAP_THREAD_LIMIT = 40;

export type MailListCacheKey = {
  account: string;
  folder: string;
};

export type MailNavState = {
  account: string;
  folder: string;
};

export type MailBootstrap = {
  account: string;
  folder: string;
  accounts: MailAccount[];
  threads: MailThread[];
  savedAt: number;
};

type ListRecord = {
  key: string;
  threads: MailThread[];
  savedAt: number;
};

type DetailRecord = {
  key: string;
  thread: MailThread;
  savedAt: number;
};

type AccountsRecord = {
  key: "accounts";
  accounts: MailAccount[];
  savedAt: number;
};

function listKey(parts: MailListCacheKey): string {
  return `${parts.account || "all"}::${parts.folder || "inbox"}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error || new Error("idb open failed"));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(LIST_STORE)) {
        db.createObjectStore(LIST_STORE, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(DETAIL_STORE)) {
        db.createObjectStore(DETAIL_STORE, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(ACCOUNTS_STORE)) {
        db.createObjectStore(ACCOUNTS_STORE, { keyPath: "key" });
      }
    };
  });
}

async function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | undefined> {
  try {
    const db = await openDb();
    return await new Promise<T | undefined>((resolve, reject) => {
      const tx = db.transaction(storeName, mode);
      const store = tx.objectStore(storeName);
      const req = fn(store);
      tx.oncomplete = () => {
        if (req) resolve(req.result);
        else resolve(undefined);
      };
      tx.onerror = () => reject(tx.error || new Error("idb tx failed"));
      if (req) {
        req.onerror = () => reject(req.error || new Error("idb request failed"));
      }
    });
  } catch {
    return undefined;
  }
}

/** Wipe every saved list, thread, and account (used when a different person signs in). */
export async function clearMailOfflineCache(): Promise<void> {
  for (const key of [BOOTSTRAP_KEY, NAV_KEY, "atw-hub-mail-window-sync"]) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
  for (const storeName of [LIST_STORE, DETAIL_STORE, ACCOUNTS_STORE]) {
    await withStore(storeName, "readwrite", (store) => {
      store.clear();
    });
  }
}

/** Ask the browser to keep this origin's storage (helps iOS/Android PWAs). */
export function requestPersistentStorage(): void {
  try {
    void navigator.storage?.persist?.();
  } catch {
    /* ignore */
  }
}

export const DEFAULT_MAIL_CACHE_WINDOW = "1m";

export function readMailCacheWindowId(): string {
  try {
    const id = localStorage.getItem(CACHE_WINDOW_KEY) || DEFAULT_MAIL_CACHE_WINDOW;
    return id === "all" ? "36m" : id;
  } catch {
    return DEFAULT_MAIL_CACHE_WINDOW;
  }
}

/** Oldest timestamp (ms) this device keeps, or null when the window is unbounded. */
export function mailCacheWindowCutoff(): number | null {
  const windowMs = cacheWindowMs(readMailCacheWindowId());
  return windowMs == null ? null : Date.now() - windowMs;
}

/** Length of the Settings mail-cache window in ms (null = unbounded). */
export function readMailCacheWindowMs(): number | null {
  return cacheWindowMs(readMailCacheWindowId());
}

function threadTimeMs(thread: MailThread): number | null {
  const t = new Date(thread.date || "").getTime();
  return Number.isNaN(t) ? null : t;
}

/** Drop threads that do not belong to a single-account list view. */
export function filterThreadsForAccountView(
  threads: MailThread[],
  account: string | undefined,
): MailThread[] {
  const requested = (account || "all").trim();
  if (!requested || requested === "all") return threads;
  return threads.filter((thread) => (thread.accountId || "") === requested);
}

/**
 * Newest page from the network plus older cached rows. Per account, cached rows newer
 * than that account's oldest network row are dropped (the network page is authoritative
 * there, so archived/deleted mail disappears); older rows are kept so the device
 * holds the whole cache window.
 *
 * When the network page is a single mailbox, cached threads from other accounts are
 * discarded. A polluted per-account IDB key (e.g. after All Inboxes) used to re-admit
 * those rows forever because `oldest == null` treated them as "keep".
 */
export function mergeNewestWithCachedOlder(
  network: MailThread[],
  cached: MailThread[] | null | undefined,
  rowKeyOf: (thread: MailThread) => string,
): MailThread[] {
  if (!cached?.length) return network;
  const oldestByAccount = new Map<string, number>();
  const networkAccounts = new Set<string>();
  for (const thread of network) {
    const account = thread.accountId || "";
    networkAccounts.add(account);
    const t = threadTimeMs(thread);
    if (t == null) continue;
    const prev = oldestByAccount.get(account);
    if (prev == null || t < prev) oldestByAccount.set(account, t);
  }
  const singleAccount =
    network.length > 0 && networkAccounts.size === 1 ? [...networkAccounts][0]! : null;
  const seen = new Set(network.map(rowKeyOf));
  const older = cached.filter((thread) => {
    const key = rowKeyOf(thread);
    if (seen.has(key)) return false;
    const account = thread.accountId || "";
    if (singleAccount != null && account !== singleAccount) return false;
    const oldest = oldestByAccount.get(account);
    if (oldest == null) return true;
    const t = threadTimeMs(thread);
    return t != null && t < oldest;
  });
  if (!older.length) return network;
  return [...network, ...older].sort((a, b) => (threadTimeMs(b) ?? 0) - (threadTimeMs(a) ?? 0));
}

function cacheWindowMs(id: string): number | null {
  const match = /^(\d+)m$/.exec(id);
  if (!match) return 365 * 24 * 60 * 60 * 1000;
  const months = Number(match[1]);
  // Approximate calendar months.
  return months * 30 * 24 * 60 * 60 * 1000;
}

/** Drop threads older than the Settings mail-cache window. */
export function filterThreadsByCacheWindow(threads: MailThread[]): MailThread[] {
  const windowMs = cacheWindowMs(readMailCacheWindowId());
  if (windowMs == null) return threads;
  const cutoff = Date.now() - windowMs;
  return threads.filter((thread) => {
    const raw = thread.date;
    if (!raw) return true;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) return true;
    return t >= cutoff;
  });
}

/** Strip bulky attachment bytes before caching — keep flags/names only. */
function slimThread(thread: MailThread): MailThread {
  const messages = (thread.messages || []).map((message) => {
    const attachments = Array.isArray(message.attachments)
      ? message.attachments.map((file) => ({
          id: file.id,
          filename: file.filename,
          mimeType: file.mimeType,
          size: file.size,
          // Tip 126: keep Content-ID so cid: inline images still resolve after reopen.
          contentId: file.contentId,
          url: file.url && !String(file.url).startsWith("data:") ? file.url : undefined,
          downloadUrl:
            file.downloadUrl && !String(file.downloadUrl).startsWith("data:")
              ? file.downloadUrl
              : undefined,
        }))
      : message.attachments;
    // Drop full HTML/text from list bootstrap — detail IDB keeps hydrated bodies.
    return {
      ...message,
      attachments,
      bodyHtml: undefined,
      bodyText: undefined,
    };
  });
  return { ...thread, messages };
}

function slimThreadForBootstrap(thread: MailThread): MailThread {
  const slim = slimThread(thread);
  // List row only needs preview fields + badges.
  return {
    id: slim.id,
    threadId: slim.threadId,
    accountId: slim.accountId,
    subject: slim.subject,
    snippet: slim.snippet,
    fromName: slim.fromName,
    fromEmail: slim.fromEmail,
    from: slim.from,
    date: slim.date,
    unread: slim.unread,
    starred: slim.starred,
    messageCount: slim.messageCount,
    hasAttachments: slim.hasAttachments,
  };
}

export function readMailNav(): MailNavState | null {
  try {
    const raw = localStorage.getItem(NAV_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MailNavState;
    if (!parsed || typeof parsed !== "object") return null;
    return {
      account: String(parsed.account || "all"),
      folder: String(parsed.folder || "inbox"),
    };
  } catch {
    return null;
  }
}

export function writeMailNav(nav: MailNavState): void {
  try {
    localStorage.setItem(
      NAV_KEY,
      JSON.stringify({
        account: nav.account || "all",
        folder: nav.folder || "inbox",
      }),
    );
  } catch {
    /* quota / private mode */
  }
}

/** Sync first-paint snapshot (localStorage) — available before IndexedDB opens. */
export function readMailBootstrap(): MailBootstrap | null {
  try {
    const raw = localStorage.getItem(BOOTSTRAP_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MailBootstrap;
    if (!parsed?.threads?.length) return null;
    return {
      account: String(parsed.account || "all"),
      folder: String(parsed.folder || "inbox"),
      accounts: Array.isArray(parsed.accounts) ? parsed.accounts : [],
      threads: filterThreadsByCacheWindow(parsed.threads),
      savedAt: typeof parsed.savedAt === "number" ? parsed.savedAt : 0,
    };
  } catch {
    return null;
  }
}

function writeMailBootstrap(parts: MailListCacheKey, threads: MailThread[], accounts?: MailAccount[]) {
  try {
    const prior = readMailBootstrap();
    const record: MailBootstrap = {
      account: parts.account || "all",
      folder: parts.folder || "inbox",
      accounts: accounts?.length ? accounts : prior?.accounts || [],
      threads: filterThreadsByCacheWindow(threads)
        .slice(0, BOOTSTRAP_THREAD_LIMIT)
        .map(slimThreadForBootstrap),
      savedAt: Date.now(),
    };
    localStorage.setItem(BOOTSTRAP_KEY, JSON.stringify(record));
  } catch {
    /* ignore quota */
  }
}

export async function readCachedMailAccounts(): Promise<MailAccount[] | null> {
  const row = await withStore<AccountsRecord>(ACCOUNTS_STORE, "readonly", (store) =>
    store.get("accounts"),
  );
  if (!row?.accounts?.length) {
    const boot = readMailBootstrap();
    return boot?.accounts?.length ? boot.accounts : null;
  }
  return row.accounts;
}

export async function writeCachedMailAccounts(accounts: MailAccount[]): Promise<void> {
  const record: AccountsRecord = {
    key: "accounts",
    accounts,
    savedAt: Date.now(),
  };
  await withStore(ACCOUNTS_STORE, "readwrite", (store) => {
    store.put(record);
  });
  // Keep bootstrap accounts in sync for next cold start.
  try {
    const boot = readMailBootstrap();
    if (boot) {
      localStorage.setItem(
        BOOTSTRAP_KEY,
        JSON.stringify({ ...boot, accounts, savedAt: Date.now() }),
      );
    } else {
      writeMailBootstrap({ account: "all", folder: "inbox" }, [], accounts);
    }
  } catch {
    /* ignore */
  }
}

export async function readCachedMailList(
  parts: MailListCacheKey,
): Promise<MailThread[] | null> {
  const key = listKey(parts);
  const row = await withStore<ListRecord>(LIST_STORE, "readonly", (store) =>
    store.get(key),
  );
  if (row?.threads?.length) {
    return filterThreadsForAccountView(
      filterThreadsByCacheWindow(row.threads),
      parts.account,
    );
  }
  // Fall back to sync bootstrap when IDB is empty/slow for this key.
  const boot = readMailBootstrap();
  if (
    boot?.threads?.length &&
    boot.account === (parts.account || "all") &&
    boot.folder === (parts.folder || "inbox")
  ) {
    return filterThreadsForAccountView(boot.threads, parts.account);
  }
  return null;
}

export async function writeCachedMailList(
  parts: MailListCacheKey,
  threads: MailThread[],
  accounts?: MailAccount[],
  /** Background sync: update IDB only, leave the cold-start bootstrap and nav alone. */
  opts?: { quiet?: boolean },
): Promise<void> {
  const key = listKey(parts);
  const filtered = filterThreadsByCacheWindow(
    filterThreadsForAccountView(threads, parts.account),
  );
  const record: ListRecord = {
    key,
    threads: filtered.map(slimThread),
    savedAt: Date.now(),
  };
  await withStore(LIST_STORE, "readwrite", (store) => {
    store.put(record);
  });
  if (opts?.quiet) return;
  writeMailBootstrap(parts, filtered, accounts);
  writeMailNav({ account: parts.account || "all", folder: parts.folder || "inbox" });
}

export async function readCachedMailDetail(rowKey: string): Promise<MailThread | null> {
  if (!rowKey) return null;
  const row = await withStore<DetailRecord>(DETAIL_STORE, "readonly", (store) =>
    store.get(rowKey),
  );
  return row?.thread || null;
}

export async function writeCachedMailDetail(
  rowKey: string,
  thread: MailThread,
): Promise<void> {
  if (!rowKey) return;
  const record: DetailRecord = {
    key: rowKey,
    thread: slimThread(thread),
    savedAt: Date.now(),
  };
  // Detail store keeps message shells; prefer retaining body when present.
  const withBodies: MailThread = {
    ...slimThread(thread),
    messages: (thread.messages || []).map((message) => ({
      ...message,
      attachments: Array.isArray(message.attachments)
        ? message.attachments.map((file) => ({
            id: file.id,
            filename: file.filename,
            mimeType: file.mimeType,
            size: file.size,
            contentId: file.contentId,
            url: file.url && !String(file.url).startsWith("data:") ? file.url : undefined,
            downloadUrl:
              file.downloadUrl && !String(file.downloadUrl).startsWith("data:")
                ? file.downloadUrl
                : undefined,
          }))
        : message.attachments,
      // Keep text/html for reopen — strip only data: attachment payloads.
      bodyHtml: message.bodyHtml,
      bodyText: message.bodyText,
    })),
  };
  record.thread = withBodies;
  await withStore(DETAIL_STORE, "readwrite", (store) => {
    store.put(record);
  });
}

/**
 * When the network list under-reports count/attach, keep richer values from
 * a prior hydrated cache (so badges don't vanish on every refresh).
 */
export function mergeListMeta(
  network: MailThread[],
  cached: MailThread[] | null | undefined,
  rowKeyOf: (thread: MailThread) => string,
  keepHigherCount = true,
): MailThread[] {
  if (!cached?.length) return network;
  const byKey = new Map(cached.map((thread) => [rowKeyOf(thread), thread]));
  return network.map((thread) => {
    const prev = byKey.get(rowKeyOf(thread));
    if (!prev) return thread;
    const netCount =
      typeof thread.messageCount === "number" && thread.messageCount > 0
        ? thread.messageCount
        : 1;
    const prevCount =
      typeof prev.messageCount === "number" && prev.messageCount > 0
        ? prev.messageCount
        : Array.isArray(prev.messages) && prev.messages.length > 1
          ? prev.messages.length
          : 1;
    return {
      ...thread,
      // Sent/Drafts must not keep a full-conversation count from an older cache.
      messageCount: keepHigherCount ? Math.max(netCount, prevCount) : netCount,
      hasAttachments: Boolean(thread.hasAttachments) || Boolean(prev.hasAttachments),
    };
  });
}
